import { FiCheck } from "react-icons/fi";
import StyledPhoneInput from "../StyledPhoneInput";
import { countryCodes } from "../../utils/countryCodes";
import { BUSINESS_ACTIVITIES } from "../../constants/kyc";

/**
 * The building blocks of the verification forms — a company's and an individual's.
 * They look like the create-business form, so the questionnaire reads as part of it.
 */

const COUNTRY_LIST_ID = "kyc-countries";

export const kycStyles = (isDark) => ({
  input: `w-full rounded-radius-sm border px-3 py-2.5 text-subheadline transition focus:outline-none focus:ring-2 focus:ring-accent-default disabled:cursor-not-allowed disabled:opacity-60 ${
    isDark
      ? "border-white/10 bg-white/5 text-slate-100 placeholder-slate-500 focus:border-accent-default focus:bg-white/10"
      : "border-slate-200 bg-white text-slate-900 placeholder-slate-400 focus:border-accent-default"
  }`,
  label: `block text-footnote-semibold text-label-primary`,
  hint: `text-caption-1 leading-snug text-label-tertiary`,
  heading: `text-subheadline-semibold text-label-primary`,
  muted: "text-label-secondary",
  card: "border-separator-default bg-bg-raised",
});

export function SectionTitle({ title, hint, isDark }) {
  const s = kycStyles(isDark);
  return (
    <div className="mb-3">
      <h3 className={s.heading}>{title}</h3>
      {hint && <p className={`mt-0.5 text-footnote ${s.muted}`}>{hint}</p>}
    </div>
  );
}

export function Field({ label, hint, error, required, optional, isDark, t, htmlFor, children }) {
  const s = kycStyles(isDark);
  return (
    <div className="min-w-0 space-y-1.5">
      <label htmlFor={htmlFor} className={s.label}>
        {label}
        {required && <span className="text-status-danger"> *</span>}
        {optional && <span className={`font-normal ${s.muted}`}> ({t("kyc.optional")})</span>}
      </label>
      {children}
      {error ? (
        <p className="text-footnote text-status-danger" role="alert">
          {error}
        </p>
      ) : (
        hint && <p className={s.hint}>{hint}</p>
      )}
    </div>
  );
}

export function TextField({ id, value, onChange, error, isDark, className = "", ...rest }) {
  const s = kycStyles(isDark);
  return (
    <input
      id={id}
      type="text"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      aria-invalid={Boolean(error)}
      className={`${s.input} ${error ? "border-status-danger" : ""} ${className}`}
      {...rest}
    />
  );
}

/** One choice out of a few, as pills. */
export function OptionPills({ options, value, onChange, disabled, error, isDark, columns = "sm:grid-cols-2", label }) {
  return (
    <div role="radiogroup" aria-label={label} className={`grid grid-cols-1 gap-2 ${columns}`}>
      {options.map((o) => {
        const selected = value === o.id;
        return (
          <button
            key={String(o.id)}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => onChange(o.id)}
            className={`flex items-center gap-2 rounded-radius-sm border px-3 py-2.5 text-left text-subheadline-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
              selected
                ? "border-accent-default bg-accent-tinted text-accent-text"
                : error
                ? "border-status-danger"
                : isDark
                ? "border-white/10 bg-white/[0.03] text-slate-200 hover:border-white/20"
                : "border-slate-200 bg-white text-slate-700 hover:border-accent-default"
            }`}
          >
            <span
              className={`grid size-4 shrink-0 place-items-center rounded-full border ${
                selected ? "border-accent-default bg-accent-default" : "border-separator-opaque"
              }`}
            >
              {selected && <span className="size-1.5 rounded-full bg-ink" />}
            </span>
            <span className="min-w-0">{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/** A yes/no the person has to answer — nothing is preselected. */
export function YesNo({ value, onChange, disabled, error, isDark, t, label }) {
  return (
    <OptionPills
      label={label}
      options={[
        { id: true, label: t("kyc.yes") },
        { id: false, label: t("kyc.no") },
      ]}
      value={value}
      onChange={onChange}
      disabled={disabled}
      error={error}
      isDark={isDark}
      columns="grid-cols-2"
    />
  );
}

/** "Please select the type of business" — any number of them, plus "Other" in words. */
export function ActivityPicker({ value = [], other = "", onChange, onOtherChange, error, otherError, disabled, isDark, t }) {
  const s = kycStyles(isDark);
  const toggle = (id) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

  return (
    <div className="space-y-3">
      <div
        role="group"
        aria-label={t("kyc.sections.activity")}
        className={`grid grid-cols-1 gap-1.5 rounded-radius-sm sm:grid-cols-2 ${error ? "ring-1 ring-red-400" : ""}`}
      >
        {BUSINESS_ACTIVITIES.map((id) => {
          const checked = value.includes(id);
          return (
            <button
              key={id}
              type="button"
              role="checkbox"
              aria-checked={checked}
              disabled={disabled}
              onClick={() => toggle(id)}
              className={`flex items-center gap-2.5 rounded-radius-sm border px-3 py-2 text-left text-subheadline transition disabled:cursor-not-allowed disabled:opacity-60 ${
                checked
                  ? "border-accent-default bg-accent-tinted"
                  : isDark
                  ? "border-white/10 bg-white/[0.03] hover:border-white/20"
                  : "border-slate-200 bg-white hover:border-accent-default"
              }`}
            >
              <span
                className={`grid size-4 shrink-0 place-items-center rounded border ${
                  checked ? "border-accent-default bg-accent-default text-accent-on" : "border-separator-opaque"
                }`}
              >
                {checked && <FiCheck className="size-3" strokeWidth={3} />}
              </span>
              <span className={`min-w-0 ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                {t(`kyc.activities.${id}`)}
              </span>
            </button>
          );
        })}
      </div>
      {error && (
        <p className="text-footnote text-status-danger" role="alert">
          {error}
        </p>
      )}
      {value.includes("other") && (
        <Field label={t("kyc.fields.activityOther")} required error={otherError} isDark={isDark} t={t} htmlFor="kyc-activity-other">
          <TextField
            id="kyc-activity-other"
            value={other}
            onChange={onOtherChange}
            error={otherError}
            isDark={isDark}
            disabled={disabled}
            maxLength={200}
            placeholder={t("kyc.placeholders.activityOther")}
          />
        </Field>
      )}
      <p className={s.hint}>{t("kyc.sections.activityHint")}</p>
    </div>
  );
}

/** Suggestions for the citizenship fields; typing anything else still works. */
export function CountryList() {
  return (
    <datalist id={COUNTRY_LIST_ID}>
      {[...new Set(countryCodes.map((c) => c.name))].map((name) => (
        <option key={name} value={name} />
      ))}
    </datalist>
  );
}

/**
 * A phone field in the same shape as the others. The shared phone input is styled for
 * the sign-up form, so its size is brought in line here.
 */
export function PhoneField({ id, value, onChange, error, disabled, isDark }) {
  if (disabled) {
    return <TextField id={id} value={value} onChange={() => {}} disabled isDark={isDark} />;
  }
  return (
    <div className={error ? "kyc-phone--error" : ""}>
      <StyledPhoneInput value={value} onChange={(v) => onChange(v || "")} isDark={isDark} className="kyc-phone" />
      <style>{`
        .kyc-phone.phone-input-wrapper .PhoneInputInput {
          border-radius: 0.75rem;
          border-width: 1px;
          padding: 0.625rem 0.75rem 0.625rem 3.25rem;
          font-size: 0.875rem;
          line-height: 1.25rem;
        }
        .kyc-phone--error .kyc-phone.phone-input-wrapper .PhoneInputInput {
          border-color: rgb(248, 113, 113);
        }
      `}</style>
    </div>
  );
}

/**
 * The questions asked of a director or a beneficial owner. Field ids and error keys
 * are prefixed with the person's path, e.g. "beneficialOwners.0".
 */
export function PersonFields({ prefix, person, onChange, errors = {}, disabled, isDark, t, withShare = false }) {
  const err = (field) => errors[`${prefix}.${field}`];
  const id = (field) => `kyc-${prefix.replace(/\./g, "-")}-${field}`;
  const set = (field) => (value) => onChange(`${prefix}.${field}`, value);

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <Field label={t("kyc.fields.fullName")} required error={err("fullName")} isDark={isDark} t={t} htmlFor={id("fullName")}>
        <TextField
          id={id("fullName")}
          value={person.fullName}
          onChange={set("fullName")}
          error={err("fullName")}
          isDark={isDark}
          disabled={disabled}
          maxLength={200}
          autoComplete="name"
          placeholder={t("kyc.placeholders.fullName")}
        />
      </Field>
      <Field
        label={t("kyc.fields.personalId")}
        hint={t("kyc.fields.personalIdHint")}
        required
        error={err("personalId")}
        isDark={isDark}
        t={t}
        htmlFor={id("personalId")}
      >
        <TextField
          id={id("personalId")}
          value={person.personalId}
          onChange={(v) => set("personalId")(v.toUpperCase())}
          error={err("personalId")}
          isDark={isDark}
          disabled={disabled}
          maxLength={20}
          autoComplete="off"
          placeholder={t("kyc.placeholders.personalId")}
        />
      </Field>
      <Field label={t("kyc.fields.citizenship")} required error={err("citizenship")} isDark={isDark} t={t} htmlFor={id("citizenship")}>
        <TextField
          id={id("citizenship")}
          value={person.citizenship}
          onChange={set("citizenship")}
          error={err("citizenship")}
          isDark={isDark}
          disabled={disabled}
          maxLength={100}
          list={COUNTRY_LIST_ID}
          autoComplete="off"
          placeholder={t("kyc.placeholders.citizenship")}
        />
      </Field>
      {withShare && (
        <Field label={t("kyc.fields.sharePercent")} required error={err("sharePercent")} isDark={isDark} t={t} htmlFor={id("sharePercent")}>
          <TextField
            id={id("sharePercent")}
            value={person.sharePercent}
            onChange={(v) => set("sharePercent")(v.replace(/[^\d.,]/g, ""))}
            error={err("sharePercent")}
            isDark={isDark}
            disabled={disabled}
            inputMode="decimal"
            maxLength={6}
            placeholder="25–100"
          />
        </Field>
      )}
      <Field label={t("kyc.fields.email")} required error={err("email")} isDark={isDark} t={t} htmlFor={id("email")}>
        <TextField
          id={id("email")}
          inputMode="email"
          value={person.email}
          onChange={set("email")}
          error={err("email")}
          isDark={isDark}
          disabled={disabled}
          maxLength={200}
          autoComplete="email"
          placeholder={t("kyc.placeholders.email")}
        />
      </Field>
      <Field label={t("kyc.fields.phone")} required error={err("phone")} isDark={isDark} t={t}>
        <PhoneField id={id("phone")} value={person.phone} onChange={set("phone")} error={err("phone")} disabled={disabled} isDark={isDark} />
      </Field>
      <div className="sm:col-span-2">
        <Field label={t("kyc.fields.pep")} hint={t("kyc.fields.pepHint")} required error={err("pep")} isDark={isDark} t={t}>
          <div className="max-w-xs">
            <YesNo
              label={t("kyc.fields.pep")}
              value={person.pep}
              onChange={set("pep")}
              error={err("pep")}
              disabled={disabled}
              isDark={isDark}
              t={t}
            />
          </div>
        </Field>
      </div>
    </div>
  );
}
